import type { ReactNode } from 'react'
import { AppLayout } from '../components/AppLayout'

/**
 * /terms · /privacy · /refunds — legal pages for the DiaEx directory.
 *
 * v0 MODEL (2026-09): DiaEx is a FREE, open supplier/buyer DIRECTORY. There is
 * NO on-platform buying or selling — no checkout, no order acceptance, no fees,
 * shipping, tax, financing, or concierge. Verified members view a supplier's
 * price and contact details and transact DIRECTLY with the supplier, off-platform,
 * on the supplier's own terms; DiaEx is an introduction service only and is NOT a
 * party to any transaction (it does not accept orders, process payment, hold funds,
 * transfer title, or bear risk).
 *
 * Content covers the free directory model plus the industry-standard B2B diamond
 * terms retained for v0 (Kimberley Process / System of Warranties, broker shares,
 * AI assistant disclaimer). On-platform sourcing + escrow arrive in v1 — revise
 * these Terms and the Refund Policy then. Drafted for launch; counsel review
 * pending (esp. governing law) — update LAST_UPDATED when revised.
 */
const LAST_UPDATED = 'September 22, 2026'

function LegalPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="font-serif text-3xl text-ink">{title}</h1>
        <p className="mt-1 text-xs text-soft">Last updated {LAST_UPDATED}</p>
        <div className="mt-8 space-y-6 text-sm leading-relaxed text-ink/90">{children}</div>
      </div>
    </AppLayout>
  )
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-semibold text-ink text-base">{heading}</h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  )
}

export function Terms() {
  return (
    <LegalPage title="Terms of Service">
      <p>
        Welcome to DiaEx, a business-to-business diamond directory operated by Aska
        (&ldquo;DiaEx&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). DiaEx is a free, open
        directory that connects diamond buyers and suppliers. These Terms govern your use of
        the platform — browsing the catalog, viewing a supplier&rsquo;s price and contact
        details once verified, listing inventory as a supplier, and sharing selections with
        your own customers. DiaEx does not sell diamonds and is not a party to any transaction
        between a buyer and a supplier. By creating an account or using the platform you agree
        to these Terms on behalf of yourself and the business you represent.
      </p>
      <Section heading="1. The platform; eligibility">
        <p>
          DiaEx is a trade-only marketplace listing diamonds offered by independent
          suppliers. It is intended solely for jewelers, brokers, dealers, and other diamond
          trade professionals acting in the course of business — it is not a consumer
          service. You must be at least 18, authorized to bind your business, and maintain
          one account per business. Access to pricing requires a registered account; access
          to supplier identities and purchasing requires identity verification (KYC). You
          represent that all information you provide at registration and KYC is accurate and
          that you will keep it current. We may decline, suspend, or revoke verification at
          our discretion.
        </p>
      </Section>
      <Section heading="2. How you transact">
        <p>
          DiaEx is an introduction service. Once you are verified, you may view a
          supplier&rsquo;s listed price and contact details and transact with that supplier
          <strong> directly and off-platform, on the supplier&rsquo;s own terms</strong>. Any
          resulting transaction is solely between you and the supplier.
        </p>
        <p>
          <strong>DiaEx is not a party to it.</strong> We do not accept or confirm orders, do
          not process payment, do not hold or handle funds, do not transfer title, and do not
          bear risk of loss. We make no representation or guarantee about a supplier, a stone,
          a price, availability, delivery, or the outcome of any dealing between you and a
          supplier. There is no fee to browse the catalog or to contact a supplier. You are
          responsible for conducting your own due diligence — verifying the supplier, the
          stone, its certificate, price, and terms — before you transact.
        </p>
      </Section>
      <Section heading="3. Listings, certificates, and accuracy">
        <p>
          Catalog data — certificates, measurements, media, growth type, and prices — is
          supplied by suppliers or their grading laboratories (e.g. GIA, IGI). We work to
          keep it accurate and current but do not warrant it; the grading report of record
          controls over any summary shown on the platform. Where an image is a
          representative substitute rather than a photograph of the actual stone, it is
          labeled as such. Stones may sell or become unavailable without notice.
        </p>
      </Section>
      <Section heading="4. Supplier terms">
        <p>
          Suppliers must list only inventory they own or are authorized to sell, keep
          availability and pricing current (including via approved feed integrations, which
          you authorize us to poll), and ensure listings match the certificates shown.
          Suppliers are solely responsible for any transaction they enter into with buyers
          who contact them directly, including price, payment, delivery, and the accuracy of
          what they sell. Misdescribed stones or repeated inaccurate listings may result in
          delisting.
        </p>
      </Section>
      <Section heading="5. Conflict-free warranty (Kimberley Process)">
        <p>
          Suppliers warrant that diamonds offered on DiaEx have been purchased from
          legitimate sources not involved in funding conflict, in compliance with the
          Kimberley Process Certification Scheme and the World Diamond Council System of
          Warranties, and that lab-grown stones are disclosed as such. Each participant in
          the chain agrees to pass this warranty with every transaction. We may remove any
          listing and terminate any account we reasonably believe breaches this Section or
          applicable sanctions, anti-money-laundering, or export-control laws.
        </p>
      </Section>
      <Section heading="6. Shared selections (broker shares)">
        <p>
          The share feature lets you publish a selection of stones at prices you set. You
          are solely responsible for the prices and representations you make to your own
          customers through a share link; DiaEx is not a party to any arrangement between
          you and them. Availability of shared stones is not guaranteed. We may disable
          share links that misrepresent the platform or its inventory.
        </p>
      </Section>
      <Section heading="7. AI assistant">
        <p>
          Gemma, our AI assistant, provides informational answers generated automatically
          from catalog and account data. Its responses may contain errors and are not
          offers, valuations, gemological advice, or investment advice — verify details
          against the listing and grading report before transacting.
        </p>
      </Section>
      <Section heading="8. Acceptable use">
        <p>
          You may not scrape or bulk-export the catalog, misrepresent your identity or
          business, circumvent verification, use supplier contact details for purposes other
          than bona fide trade enquiries, resell or disclose platform data to third parties,
          probe or interfere with the platform&rsquo;s operation, or use the platform in
          violation of sanctions or export-control laws. We may suspend or terminate
          accounts that breach these Terms.
        </p>
      </Section>
      <Section heading="9. Disclaimers and liability">
        <p>
          The platform is provided &ldquo;as is&rdquo;. DiaEx is a directory and introduction
          service only: we do not sell, warrant, or guarantee any stone, supplier, price, or
          the outcome of any dealing you conduct off-platform. To the fullest extent permitted
          by law, DiaEx disclaims implied warranties (including merchantability and fitness for
          a particular purpose) and is not liable for indirect or consequential loss, lost
          profits, or loss of data. Our aggregate liability for any claim is limited to $100.
          Nothing in these Terms limits liability that cannot be limited by law.
        </p>
      </Section>
      <Section heading="10. Indemnity; termination">
        <p>
          You will indemnify DiaEx against third-party claims arising from your breach of
          these Terms, your off-platform transactions, or your shares. You may close your
          account at any time (in-app or by email); we may suspend or terminate access for
          breach. Sections that by nature survive termination (including warranties, liability
          limits, and governing law) survive.
        </p>
      </Section>
      <Section heading="11. General">
        <p>
          We may update these Terms; material changes will be notified to account holders
          and continued use constitutes acceptance. These Terms are governed by the laws of
          Maharashtra, India, and disputes are subject to the courts of Mumbai, unless a
          separate written agreement between you and Aska Jewels provides otherwise. If any
          provision is unenforceable, the remainder stands. Questions:{' '}
          <a href="mailto:info@thediaex.com" className="text-primary hover:underline">
            info@thediaex.com
          </a>.
        </p>
      </Section>
    </LegalPage>
  )
}

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        This policy explains what DiaEx (operated by Aska) collects, why, and your
        choices. It applies to the DiaEx website and related services.
      </p>
      <Section heading="1. What we collect">
        <p>
          <strong>Account data</strong> — name, business name, email, phone, and password
          credentials. <strong>Verification (KYC) documents</strong> — business and identity
          documents you upload, stored privately and used solely to verify your account.{' '}
          <strong>Activity data</strong> — enquiries you make, suppliers you contact, and
          selections you save or share. <strong>Usage data</strong> — pages viewed, searches,
          and device information used to operate and improve the platform, including our AI
          assistant.
        </p>
      </Section>
      <Section heading="2. How we use it">
        <p>
          To operate the directory: verifying accounts, revealing supplier contact to
          verified buyers, sending account and service messages (for example verification
          codes and replies from our assistant), and, with your consent where required,
          marketing communications you can opt out of at any time.
        </p>
      </Section>
      <Section heading="3. Sharing">
        <p>
          We share contact details to introduce a buyer and a supplier to one another — a
          verified buyer sees a supplier&rsquo;s contact information, and a supplier you
          contact sees your enquiry and contact details. We do not share order, payment, or
          shipping data, because DiaEx does not process orders, payments, or shipping. We use
          service providers only as needed to run the platform: email delivery by Resend,
          messaging by Twilio, and cloud infrastructure on Google Cloud / Firebase. We do not
          sell personal data. We disclose data where the law requires it.
        </p>
      </Section>
      <Section heading="4. Retention and security">
        <p>
          Account and activity records are kept for as long as your account is active and
          thereafter as required for legal, tax, and audit purposes. KYC documents are
          retained while your verification is active. Data is encrypted in transit, access
          is role-restricted, and supplier price and identity data is masked from
          unverified users.
        </p>
      </Section>
      <Section heading="5. SMS &amp; WhatsApp messaging">
        <p>
          {/* Copy promises only controls that EXIST: the sign-up checkbox,
              STOP/START by reply, and contacting us. "From your account" was
              promised here before any such setting existed — a privacy policy
              must not describe UI we have not shipped. */}
          With your consent — given by ticking the opt-in box during sign-up —
          DiaEx sends <strong>account, order, and service messages</strong> (for example:
          verification codes, order status, sourcing matches, and replies from our assistant) to the
          phone number(s) you provide, by SMS and/or WhatsApp. We do not send promotional or
          marketing texts without a separate, explicit opt-in.
        </p>
        <p>
          <strong>Opting out:</strong> reply <strong>STOP</strong> to any message to stop receiving
          messages, <strong>START</strong> to opt back in, or <strong>HELP</strong> for assistance;
          you can also change your preference any time by contacting us. Message frequency varies
          with your account activity. Message and data rates may apply and are charged by your
          carrier. Consent is not a condition of purchase.
        </p>
        <p>
          Phone numbers and messaging consent records are stored with a timestamp for compliance.
          We share the number with our messaging providers (Twilio; WhatsApp/Meta) solely to deliver
          these messages — <strong>never for their marketing, and we do not sell phone numbers</strong>.
        </p>
      </Section>

      <Section heading="6. Your rights">
        <p>
          You may access, correct, or request deletion of your personal data (subject to
          records we must keep by law) by contacting{' '}
          <a href="mailto:info@thediaex.com" className="text-primary hover:underline">
            info@thediaex.com
          </a>. If you are in a jurisdiction with specific data-protection rights, we honor
          those rights as applicable.
        </p>
      </Section>
    </LegalPage>
  )
}

export function Refunds() {
  return (
    <LegalPage title="Refund & Return Policy">
      <p>
        DiaEx is a free directory that introduces buyers and suppliers. It does not sell
        diamonds and does not process any purchase. Any purchase you make is concluded
        <strong> directly with a supplier, off-platform, on that supplier&rsquo;s own terms</strong>.
      </p>
      <p>
        Because DiaEx is not a party to your transaction, it does not collect payment, hold
        funds, or handle refunds or returns. Any refund, return, exchange, or dispute is a
        matter between you and the supplier, governed by the supplier&rsquo;s own policy —
        please agree those terms with the supplier before you transact.
      </p>
      <p>
        On-platform purchasing and a corresponding DiaEx refund policy are planned for a future
        release; this policy will be updated at that time.
      </p>
      <p>
        Questions about this policy:{' '}
        <a href="mailto:info@thediaex.com" className="text-primary hover:underline">
          info@thediaex.com
        </a>.
      </p>
    </LegalPage>
  )
}
